import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Paper,
  Divider,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Link
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import useApi from '../utils/api';
import { useAuth } from '../context/AuthContext';
import DeleteAccountDialog from './DeleteAccountDialog';

const UserProfilePopup = ({ open, onClose }) => {
  const { user, isLoading } = useAuth0();
  const { put, get } = useApi();
  const { organizationId, organizationName, userName, refreshUserContext, logout } = useAuth();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ type: '', message: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [dangerZoneOpen, setDangerZoneOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orgMembers, setOrgMembers] = useState([]);


  useEffect(() => {
    if (open) {
      setUsername(userName || user?.nickname || user?.name || '');
      setEmail(user?.email || '');
    }
  }, [open, user, userName]);

  useEffect(() => {
    if (!deleteDialogOpen) {
      return undefined;
    }

    let cancelled = false;

    const fetchMembers = async () => {
      try {
        const response = await get(`${process.env.REACT_APP_API_URL}/auth/organization/members`);
        if (!cancelled && response?.members) {
          setOrgMembers(response.members);
        }
      } catch (error) {
        console.error('Failed to fetch organization members:', error);
      }
    };

    fetchMembers();

    return () => {
      cancelled = true;
    };
  }, [deleteDialogOpen, get]);

  const handleAccountDeleted = async () => {
    onClose();
    await logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleUpdateUsername = async () => {
    if (!username.trim()) {
      setAlertMessage({ type: 'error', message: 'Username cannot be empty' });
      
      return;
    }

    try {
      setLoading(true);
      setAlertMessage({ type: '', message: '' });

      const response = await put(`${process.env.REACT_APP_API_URL}/auth/update-username`, { username });

      if (response?.username) {
        setAlertMessage({ type: 'success', message: 'Username updated successfully!' });
        setUsername(response.username);
        refreshUserContext();
      } else {
        setAlertMessage({ type: 'error', message: response?.error || 'Failed to update username.' });
      }
    } catch (error) {
      setAlertMessage({ type: 'error', message: 'An error occurred while updating username.' });
      console.error(error);
    } finally {
      setLoading(false);
      setIsEditing(false);
    }
  };

  if (isLoading) return <CircularProgress />;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          bgcolor: 'white',
          color: '#424242',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        User Profile
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {alertMessage.message && (
          <Alert severity={alertMessage.type} sx={{ mb: 2 }}>
            {alertMessage.message}
          </Alert>
        )}

        {/* Profile Summary Card */}
        <Paper elevation={3} sx={{ p: 2, borderRadius: 2, mb: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
            <strong>Organization ID:</strong> {organizationId || 'N/A'}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
            <strong>Organization Name:</strong> {organizationName || 'N/A'}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
            <strong>Email:</strong> {email}
          </Typography>
          <Divider sx={{ mb: 1 }} />

          {/* Inline Editable Username */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            {isEditing ? (
              <>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
                <Button size="small" variant="contained" color="primary" onClick={handleUpdateUsername} disabled={loading}>
                  Save
                </Button>
                <Button size="small" variant="outlined" color="secondary" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Typography variant="body1" color="textSecondary">
                  <strong>Username:</strong> {username}
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<EditIcon />} 
                  onClick={() => setIsEditing(true)}
                >
                  Update
                </Button>
              </>
            )}
          </Box>
        </Paper>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => setDangerZoneOpen(true)}
            sx={{ color: 'text.secondary' }}
          >
            Delete account
          </Link>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'center',
          bgcolor: 'white',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
          p: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#424242',
            borderColor: 'rgba(0, 0, 0, 0.2)',
            '&:hover': {
              borderColor: '#424242',
              backgroundColor: 'rgba(66, 66, 66, 0.04)',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>

      <Dialog
        open={dangerZoneOpen}
        onClose={() => setDangerZoneOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'error.light',
              bgcolor: 'rgba(211, 47, 47, 0.04)',
            }}
          >
            <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
              Danger zone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Permanently remove your account from this organization.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setDangerZoneOpen(false);
                setDeleteDialogOpen(true);
              }}
            >
              Delete my account
            </Button>
          </Paper>
        </DialogContent>
      </Dialog>

      <DeleteAccountDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        variant="self"
        userEmail={email}
        members={orgMembers}
        onSuccess={handleAccountDeleted}
      />
    </Dialog>
  );
};

export default UserProfilePopup;
